import { inject, computed } from '@angular/core';
import { signalStore, withState, withComputed, withMethods, patchState, WritableStateSource, withHooks } from '@ngrx/signals';
import { UserService } from '../../services/user.service';
import { MessageService } from '../../services/message.service';
import { CreateUserCropSelectionDto, UserCropSelection } from '../../models/user-crop-selection.model';
import { firstValueFrom } from 'rxjs';
import { watchStoreErrors, extractErrorMessage } from '../../core/utils';

export interface UserState {
  user: Record<string, unknown> | null;
  userCrops: Record<string, unknown>[];
  userProfile: Record<string, unknown> | null;
  cropSelections: UserCropSelection[];
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

const initialState: UserState = {
  user: null,
  userCrops: [],
  userProfile: null,
  cropSelections: [],
  isLoading: false,
  error: null,
  isAuthenticated: false
};


export const UserStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    hasUser: computed(() => store.user() !== null),
  })),

  withMethods((store, userService = inject(UserService), messageService = inject(MessageService)) => {
    const writable = store as unknown as WritableStateSource<UserState>;

    return {
      // Authentication state methods
      setAuthState(isAuthenticated: boolean) {
        patchState(writable, { isAuthenticated });
      },

      setUser(user: Record<string, unknown> | null) {
        patchState(writable, { user });
      },

      // User profile methods
      async loadUserProfile() {
        patchState(writable, { isLoading: true, error: null });
        try {
          const userProfile = await firstValueFrom(userService.getUserProfile());
          patchState(writable, { userProfile, error: null, isLoading: false });
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to load user profile');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      // Crop selection methods
      async loadCropSelections() {
        patchState(writable, { isLoading: true, error: null });
        try {
          const cropSelections = await firstValueFrom(userService.getUserCropSelections());
          patchState(writable, { cropSelections, error: null, isLoading: false });
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to load crop selections');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      async saveCropSelection(cropSelection: CreateUserCropSelectionDto) {
        patchState(writable, { isLoading: true, error: null });
        try {
          const newSelection = await firstValueFrom(userService.saveCropSelection(cropSelection));
          patchState(writable, {
            cropSelections: [...store.cropSelections(), newSelection],
            error: null,
            isLoading: false
          });
          messageService.success('Crop selection saved successfully!');
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to save crop selection');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      // Actions
      async loadUserInfo() {
        patchState(writable, { isLoading: true, error: null });
        try {
          const user = await firstValueFrom(userService.getUserInfo());
          patchState(writable, { user, error: null, isLoading: false });
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to load user info');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      async loadUserCrops() {
        patchState(writable, { isLoading: true, error: null });
        try {
          const userCrops = await firstValueFrom(userService.getUserCrops());
          patchState(writable, { userCrops, error: null, isLoading: false });
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to load user crops');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      async updateUserInfo(userInfo: Record<string, unknown>) {
        patchState(writable, { isLoading: true, error: null });
        try {
          const updatedUser = await firstValueFrom(userService.updateUserInfo(userInfo));
          patchState(writable, {
            user: { ...store.user(), ...updatedUser },
            error: null,
            isLoading: false
          });
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err, 'Failed to update user info');
          patchState(writable, { error: errorMessage, isLoading: false });
        }
      },

      // Reset store
      resetStore() {
        patchState(writable, initialState);
      },

      // Check if user is new (has no saved crop selections)
      async isNewUser(): Promise<boolean> {
        return firstValueFrom(userService.isNewUser());
      }
    };
  }),

  withHooks({
    onInit(store) {
      const messageService = inject(MessageService);
      // Automatically show error toasts when errors occur
      watchStoreErrors(store.error, messageService, 'User Error');
    },
  })
);
